import { formatElapsedTime } from '@/utils/formatElapsedTime'
import { ASSIST_SHOW_TURN_CONTEXT } from '@/constants/feature-flags'
import chevronIcon from '@/assets/copilot/chevron-down.svg'
import type {
  AssistAskInput,
  AssistQuoteGroup,
  NestedAssistAnswer,
  QuerySuggestionCard,
} from './assist.types'
import { groupSuggestionsByQuote } from './assist.types'
import AssistQuoteCard from './AssistQuoteCard'
import AssistQuoteLine from './AssistQuoteLine'
import AssistSuggestionRow from './AssistSuggestionRow'

/** The `mm:ss` label of a quoted turn, or `null` when the proxy did not report one. */
function formatQuoteTimestamp(quoteTimestampMs: number | null): string | null {
  return quoteTimestampMs != null ? formatElapsedTime(quoteTimestampMs) : null
}

type AssistSuggestionChipsProps = {
  card: QuerySuggestionCard
  answersByQuestion: Map<string, NestedAssistAnswer>
  expandedCardIds: string[]
  disabled: boolean
  onAsk: (input: AssistAskInput) => void
  onToggleAnswer: (rowKey: string) => void
  expandedQuoteIds?: string[]
  onToggleQuote?: (quoteKey: string) => void
}

export default function AssistSuggestionChips({
  card,
  answersByQuestion,
  expandedCardIds,
  disabled,
  onAsk,
  onToggleAnswer,
  expandedQuoteIds,
  onToggleQuote = () => undefined,
}: AssistSuggestionChipsProps) {
  if (card.suggestions.length === 0) return null

  // Suggestions raised by the same sentence share a card, so a turn that
  // produced two questions is stated once instead of once per question.
  const groups = groupSuggestionsByQuote(card.suggestions)

  const renderRows = (group: AssistQuoteGroup) => (
    <div className="flex flex-col gap-1.5">
      {group.suggestions.map((suggestion) => (
        <AssistSuggestionRow
          key={suggestion.id}
          suggestion={suggestion}
          answer={answersByQuestion.get(suggestion.text)}
          expandedCardIds={expandedCardIds}
          disabled={disabled}
          onAsk={onAsk}
          onToggleAnswer={onToggleAnswer}
        />
      ))}
    </div>
  )

  return (
    <div className="flex flex-col gap-1.5">
      {groups.map((group) => {
        const rows = renderRows(group)

        // Every question states the turn it came from, answered or not: the
        // reader has to know which conversation raised it before opening
        // anything.
        if (!ASSIST_SHOW_TURN_CONTEXT || !group.quote) {
          return (
            <div key={group.key}>
              <AssistQuoteCard>{rows}</AssistQuoteCard>
            </div>
          )
        }

        const expanded = expandedQuoteIds == null || expandedQuoteIds.includes(group.key)
        const contentId = `copilot-quote-content-${group.key.replace(/[^a-zA-Z0-9_-]/g, '-')}`
        // The turn is the header, not part of the collapsible body: collapsing
        // only hides the AI content underneath it, so the speaker, the moment and
        // the transcript stay readable either way.
        const quoteLine = (
          <AssistQuoteLine
            speaker={group.speaker}
            timestamp={formatQuoteTimestamp(group.quoteTimestampMs)}
            quote={group.quote}
          />
        )

        return (
          <div key={group.key}>
            <AssistQuoteCard
              header={quoteLine}
              adornment={
                <img
                  src={chevronIcon}
                  alt=""
                  width={10.6667}
                  height={6}
                  className={[
                    'block h-[6px] w-[10.6667px] shrink-0 transition-transform',
                    expanded ? 'rotate-180' : '',
                  ].join(' ')}
                />
              }
              expanded={expanded}
              onToggle={() => onToggleQuote(group.key)}
              contentId={contentId}
            >
              {rows}
            </AssistQuoteCard>
          </div>
        )
      })}
    </div>
  )
}
